<template>
  <v-row no-gutters>
    <v-col cols="12">
      <card-title-row :straightTitle="additionalModuleName" class="pt-5 pb-3" :has-buttons="false" />
      <sidebar-row border-bottom>
        <v-alert
          dense
          text
          icon="mdi-information-outline"
          color="primary"
          class="text-body-2 text-left parcels-search-alert"
        >
          {{ $i18n.t('sidebar.uldk.parcelsAddingLimit', { limit }) }}
        </v-alert>
        <sidebar-form ref="form" v-model="params" :inputs="inputs" hide-rules-icon :has-icon-column="false">
          <template #csv="input">
            <v-row no-gutters>
              <v-col cols="12">
                <file-input
                  ref="file"
                  v-bind="input"
                  :events="{
                    'vdropzone-file-added': onFileAdded,
                    'vdropzone-removed-file': onFileRemoved,
                  }"
                  :disabled="isStickyButtonsLoading"
                />
              </v-col>
            </v-row>
          </template>
          <template #encoding="input">
            <v-row no-gutters>
              <v-col cols="12">
                <data-input
                  v-bind="input"
                  v-model="params[input.name]"
                  @change="onEncodingChange"
                  :disabled="isStickyButtonsLoading"
                />
              </v-col>
            </v-row>
          </template>
        </sidebar-form>
      </sidebar-row>
      <sidebar-row class="pt-5" v-if="fileTerrits.length">
        <v-row no-gutters align="center" class="pb-5">
          <v-col cols="auto" class="text-body-2 font-weight-bold">
            {{ `${$i18n.t('sidebar.uldk.searchResult')}: ${parcels.length}/${fileTerrits.length}` }}
          </v-col>
          <v-col cols="auto" class="pl-3">
            <dots-menu
              is-absolute
              font-size="19px"
              :disabled="!parcels.length || isStickyButtonsLoading"
              :items="dotsItems"
              @toShpFile="downloadShapefile"
              @toDataSource="openDataSourceDialog"
            >
              <template #activator="{on}">
                <base-rounded-button
                  v-on="on"
                  icon="mdi-content-save"
                  size="x-small"
                  :tooltip="!parcels.length || isStickyButtonsLoading ? '' : $i18n.t('button.saveResult')"
                  :disabled="!parcels.length || isStickyButtonsLoading"
                />
              </template>
            </dots-menu>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="auto" class="text-body-2">
            {{ `${$i18n.t('sidebar.uldk.notSearchResult')}: ${errors.length}/${fileTerrits.length}` }}
          </v-col>
        </v-row>
        <ribbon-list v-if="errors.length" :listItems="errors" item-title-key="territ" class="pt-3" />
      </sidebar-row>
    </v-col>
    <main-input-dialog
      v-if="dataSourceDialogLoaded"
      :is-visible.sync="dataSourceDialogVisible"
      :loading="dataSourceDialogLoading"
      :title-translation="$i18n.t('dialog.title.saveResultToDataSource')"
      dialog-width="30%"
      agree-text="dialog.agreeSave"
      @agree="saveToDataSource"
      :inputs="dataSourceDialogInputs"
    >
      <template #dataSourceName.input="data">
        <v-col cols="6">
          <data-input v-model="dataSourceName" v-bind="data" @change="onDataSourceChange" />
        </v-col>
      </template>
      <template
        v-for="(name, idx) of ['voivodeship', 'county', 'commune', 'region', 'sheet', 'numer', 'teryt']"
        #[`${name}.input`]="data"
      >
        <v-col cols="6" :key="idx">
          <data-input v-model="dataSourceParams[name]" v-bind="data" />
        </v-col>
      </template>
      <template #skipDuplicates.input="data">
        <v-col cols="6">
          <data-input v-model="skipDuplicates" v-bind="data">
            <template #label>
              <span :class="{ 'font--text': !data.disabled }"> {{ $i18n.t(data.translationPath) }} </span>
            </template>
          </data-input>
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import FileInput from '@/components/FileInput';
import RibbonList from '@/components/RibbonList';
import SidebarForm from '@/components/SidebarForm';
import SidebarRow from '@/components/SidebarRow';

import WKT from 'ol/format/WKT';
import { writeGeometryToGeoJSON } from '@/assets/js/mapUtils';

export default {
  name: 'ParcelsSearch',
  components: {
    CardTitleRow,
    DataInput,
    DotsMenu,
    FileInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    RibbonList,
    SidebarForm,
    SidebarRow,
  },
  computed: {
    additionalModuleName: get('admin/additionalModules@PARCELS.verbose_name'),
    inputs() {
      return [
        {
          name: 'csv',
          translationPath: 'sidebar.uldk.csv',
          cols: '7',
          nameCols: '5',
          options: this.csvFileInputOptions,
          showAddedFiles: true,
          hideErrors: ['dictUploadCanceled', 'dictMaxFilesExceeded'],
        },
        {
          name: 'encoding',
          translationPath: 'sidebar.uldk.charsCoding',
          dataType: { name: 'select' },
          items: this.encodings,
          cols: '7',
          nameCols: '5',
          clearable: false,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    csvFileInputOptions() {
      return {
        url: '/',
        autoProcessQueue: false,
        acceptedFiles: '.csv',
        addRemoveLinks: true,
        maxFiles: 1,
        init: function() {
          this.on('maxfilesexceeded', file => {
            this.removeAllFiles();
            this.addFile(file);
          });
        },
      };
    },

    dataSources: get('layers/metadata'),
    polygonDataSourcesArray() {
      return this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.geometry_type === 'polygon' || dataSource.geometry_type === 'multipolygon'
      );
    },
    dataSourceDialogInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          subtitle: 'sidebar.uldk.dataSourceSubtitle',
          dataType: {
            name: 'select',
          },
          items: this.polygonDataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        ...['voivodeship', 'county', 'commune', 'region', 'sheet', 'numer', 'teryt'].map(attribute => {
          return {
            name: attribute,
            translationPath: `sidebar.uldk.attributes.${attribute}`,
            cols: '12',
            dataType: { name: 'select' },
            items: this.dataSourceTextAttributes,
            clearable: true,
            disabled: !this.dataSourceName,
          };
        }),
        {
          name: 'skipDuplicates',
          translationPath: 'sidebar.uldk.skipDuplicates',
          persistentHint: true,
          hint: this.$i18n.t('sidebar.uldk.skipDuplicatesSubtitle'),
          cols: '12',
          leftCol: 0,
          dataType: {
            name: 'checkbox',
          },
          disabled: !this.dataSourceParams['teryt'],
        },
      ];
    },

    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },

    stickyButtons: sync('sidebar/stickyButtons'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
  },
  data: () => ({
    limit: 1000,

    dotsItems: [
      {
        name: 'toShpFile',
      },
      {
        name: 'toDataSource',
      },
    ],
    encodings: [
      {
        value: 'utf-8',
        text: 'UTF-8',
      },
      {
        value: 'windows-1250',
        text: 'Windows-1250',
      },
      {
        value: 'iso-8859-2',
        text: 'ISO 8859-2',
      },
    ],
    params: {
      encoding: '',
    },

    errors: [],
    parcels: [],
    fileTerrits: [],

    dataSourceDialogLoaded: false,
    dataSourceDialogVisible: false,
    dataSourceDialogLoading: false,
    dataSourceName: '',
    dataSourceTextAttributes: [],
    skipDuplicates: false,
    dataSourceParams: {},
  }),
  methods: {
    searchUldkParcel: call('search/searchUldkParcel'),
    exportGeojsonToShapefile: call('layers/exportGeojsonToShapefile'),
    getDataSourceAttributeUniqueValues: call('layers/getDataSourceAttributeUniqueValues'),
    uploadDataSource: call('layers/uploadDataSource'),
    async onFileAdded(file) {
      await this.readFile(file);
    },
    onEncodingChange() {
      const file = this.$refs.file?.$refs.dropzone?.getAcceptedFiles()?.[0];
      if (file) this.readFile(file);
    },
    readFile(file) {
      this.reset(true);
      this.$papa.parse(file, {
        complete: results => {
          this.fileTerrits = Array.from(new Set(results.data.map(e => e[0])));
          if (this.fileTerrits?.length > this.limit) {
            this.reset();
            this.$store.set('snackbar/PUSH_ERROR!', {
              message: this.$i18n.t('sidebar.uldk.parcelsAddingLimit', { limit: this.limit }),
            });
          } else this.isStickyButtonsValid = true;
        },
        encoding: this.params.encoding,
        delimiter: '\n',
        skipEmptyLines: true,
      });
    },

    openDataSourceDialog() {
      this.dataSourceDialogLoaded = this.dataSourceDialogVisible = true;
    },
    onDataSourceChange(nV) {
      this.dataSourceParams = {};
      this.dataSourceTextAttributes = (this.dataSources[nV]?.attributes_schema.attributes || [])
        .filter(attribute => attribute.data_type?.name === 'text')
        .map(attribute => {
          const { name: value, verbose_name: text } = attribute;
          return { text, value };
        });
    },
    async saveToDataSource() {
      this.dataSourceDialogLoading = true;
      try {
        let geojson;
        if (this.skipDuplicates) {
          const r = await this.getDataSourceAttributeUniqueValues({
            dataSourceName: this.dataSourceName,
            attributeName: this.dataSourceParams['teryt'],
          });
          const uniqueValues = Object.keys(r.values);
          const filteredParcels = this.parcels.filter(parcel => !uniqueValues.includes(parcel['teryt']));
          geojson = this.getGeojson(this.dataSourceParams, filteredParcels);
        } else geojson = this.getGeojson(this.dataSourceParams);

        const blob = new Blob([JSON.stringify(geojson)], { type: 'application/geo+json;charset=utf-8;' });
        const file = new File([blob], 'geojson.geojson');
        const formData = new FormData();
        formData.append('geojson', file);
        formData.append('srid', geojson.crs.properties.name.split(':')[1]);
        formData.append('datasource', this.dataSourceName);
        await this.uploadDataSource(formData);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.measurementsModule.successSaveFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.dataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
      } finally {
        this.dataSourceDialogLoading = false;
        this.dataSourceDialogVisible = false;
      }
    },
    async downloadShapefile() {
      this.isStickyButtonsLoading = true;
      try {
        const geojson = this.getGeojson();
        await this.exportGeojsonToShapefile(geojson);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
    getGeojson(attrsMapping, parcels = this.parcels) {
      return {
        crs: {
          properties: {
            name: this.$_config.defaultEpsg,
          },
          type: 'name',
        },
        features: parcels.map((parcel, index) => {
          return {
            geometry: parcel.geometry,
            ...(attrsMapping ? {} : { id: index + 1 }),
            properties: ['commune', 'county', 'numer', 'region', 'teryt', 'voivodeship', 'sheet'].reduce(
              (total, current) => {
                if (attrsMapping) {
                  if (attrsMapping[current]) total[attrsMapping[current]] = parcel[current] || null;
                } else {
                  total[current] = parcel[current] || null;
                }
                return total;
              },
              {}
            ),
            type: 'Feature',
          };
        }),
        type: 'FeatureCollection',
      };
    },

    async search() {
      this.isStickyButtonsLoading = true;
      this.currentIntervalIndex = 0;
      this.errors = [];
      this.parcels = [];
      this.interval = setInterval(() => {
        this.searchParcel(this.fileTerrits[this.currentIntervalIndex]);
        this.currentIntervalIndex++;
        if (this.currentIntervalIndex >= this.fileTerrits.length) {
          clearInterval(this.interval);
          this.interval = undefined;
        }
      }, 1000);
    },
    async searchParcel(territ) {
      try {
        const parcel = await this.searchUldkParcel({
          territ,
          params: ['geom_wkt', 'numer', 'teryt', 'voivodeship', 'commune', 'county', 'region'],
          mapped: true,
        });
        let { geom_wkt: wkt } = parcel;
        if (wkt.indexOf(';') >= 0) wkt = wkt.split(';')[1];
        const geom = new WKT().readGeometry(wkt, {
          dataProjection: this.$_config.defaultEpsg,
          featureProjection: this.$_config.defaultEpsg,
        });
        parcel.geometry = writeGeometryToGeoJSON(geom);

        const territValue = parcel.teryt;
        const dotsIndexes = [];
        for (let i = 0; i < territValue.length; i++) {
          if (territValue[i] === '.') {
            dotsIndexes.push(i);
          }
        }
        if (dotsIndexes.length > 2) {
          parcel.sheet = territValue.substring(dotsIndexes[1] + 1, dotsIndexes[2]);
        }
        this.parcels.push(parcel);
      } catch {
        this.errors.push({ territ });
      } finally {
        if (!this.interval && this.parcels.length + this.errors.length >= this.fileTerrits.length) {
          this.isStickyButtonsLoading = false;
          this.$root.$emit('toggleFeatureHover', this.parcels, true);
        }
      }
    },
    reset(file = false) {
      this.$root.$emit('toggleFeatureHover');
      if (!file) this.$refs.file?.$refs.dropzone?.removeAllFiles();
      this.fileTerrits = [];
      this.parcels = [];
      this.errors = [];
      this.isStickyButtonsValid = false;
    },

    onFileRemoved() {
      this.fileTerrits = [];
    },
  },
  watch: {
    'params.encoding': {
      immediate: true,
      deep: true,
      handler(nV) {
        if (!nV) this.params.encoding = 'utf-8';
      },
    },
    'dataSourceParams.teryt': {
      handler(nV) {
        if (!nV) this.skipDuplicates = false;
      },
    },
  },
  mounted() {
    this.$root.$off('parcelsSearch');
    this.$root.$off('resetParcelsSearch');
    this.$root.$on('parcelsSearch', this.search);
    this.$root.$on('resetParcelsSearch', this.reset);
    this.stickyButtons = [
      {
        action: 'parcelsSearch',
        translationPath: 'default.find',
      },
      {
        action: 'resetParcelsSearch',
        translationPath: 'dialog.reset',
        cancel: true,
      },
    ];
    this.isStickyButtonsValid = false;
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = true;
  },
  beforeDestroy() {
    this.$root.$emit('toggleFeatureHover');
  },
};
</script>
<style lang="scss" scoped>
.parcels-search-alert {
  font-size: 12px !important;
}
::v-deep {
  .data-input-checkbox {
    .v-input__slot {
      margin-bottom: unset;
    }
    .v-messages {
      padding-left: 32px;
      color: rgba(0, 0, 0, 0.38);
    }
  }
}
</style>
